import React, { Component } from 'react';
import Cookies from 'universal-cookie';
import propTypes from 'prop-types';
import { Button, ButtonGroup, FormGroup, Label, Input } from 'reactstrap';

import { noop } from 'lib/noop';
import { isBlank } from 'lib/string';
import { PrimaryButton, ClearButton } from 'ui/Components';

import { AMP_FILTER_OPTIONS, AMP_QUERIES } from '../const';

const cookies = new Cookies();

/**
 * controls for web ready tab on amp modal
 */
export default class WebReadyTab extends Component {
  static propTypes = {
    changeFilter: propTypes.func,
    clearQueryData: propTypes.func,
    filter: propTypes.string,
    queryByAssetId: propTypes.func,
    queryByWorkfrontId: propTypes.func
  };

  static defaultProps = {
    changeFilter: noop,
    clearQueryData: noop,
    filter: AMP_FILTER_OPTIONS[0],
    queryByAssetId: noop,
    queryByWorkfrontId: noop
  };

  state = {
    isEvergreen: false,
    query: '',
    queryBy: AMP_QUERIES.ASSET_ID
  };

  /**
   * check whether `queryBy` internal state is set to asset id
   * @returns {boolean} whether modal will query by asset id
   */
  get isQueryByAsset() {
    return this.state.queryBy === AMP_QUERIES.ASSET_ID;
  }

  /**
   * update `queryBy` internal state based on event target text
   * @param {Event} event - click event
   */
  changeQueryBy = event => {
    const { innerText: queryBy } = event.target;
    const newState = {
      queryBy,
      isEvergreen: false,
      query: ''
    };

    // get query value from cookie when going from asset to workfront id
    const workfrontId = cookies.get(AMP_QUERIES.WORKFRONT_ID);
    if (this.isQueryByAsset && !isBlank(workfrontId)) {
      newState.query = workfrontId;
    }

    this.setState(newState);
  };

  /**
   * call `changeFilter` prop with the given event target value
   * @param {object} event - the event to be handled
   */
  handleFilterChange = event => {
    const { changeFilter } = this.props;
    changeFilter(event.target.value);
  };

  /**
   * a generic handler for onChange events
   * update internal state based on event target name and value
   * @param {object} event - the event to be handled
   */
  handleInputChange = event => {
    const { checked, name, type, value } = event.target;
    const newValue = type === 'checkbox' ? checked : value;
    this.setState({ [name]: newValue });
  };

  /**
   * query amp when enter key is pressed
   * @param {Event} event - key press event
   */
  handleKeyPress = event => {
    if (event.key === 'Enter') {
      this.queryAMP();
    }
  };

  /**
   * query amp by calling action to request amp api
   * also save last used workfront query into cookie
   */
  queryAMP = () => {
    const { queryByAssetId, queryByWorkfrontId } = this.props;
    const { isEvergreen, query: id } = this.state;

    // request based on `queryBy` internal state
    if (this.isQueryByAsset) {
      queryByAssetId({ id, isEvergreen });
    } else {
      queryByWorkfrontId({ id });

      // get nauth token expiry in seconds or default to 12 hours
      const { REACT_APP_NAUTH_TOKEN_COOKIE_V1: nauthTokenCookie } = process.env;
      const expiry = cookies.get(`${nauthTokenCookie}Expiration`);
      const maxAge = !isBlank(expiry)
        ? Math.floor(Number(expiry) - Date.now() / 1000)
        : 12 * 3600;

      // set the workfront id cookie on AMP query
      cookies.set(AMP_QUERIES.WORKFRONT_ID, id, {
        maxAge,
        path: '/'
      });
    }
  };

  /**
   * reset internal state
   */
  resetInputState = () => {
    const { clearQueryData } = this.props;
    this.setState(
      {
        isEvergreen: false,
        query: ''
      },
      () => {
        clearQueryData();
        cookies.remove(AMP_QUERIES.WORKFRONT_ID, { path: '/' });
      }
    );
  };

  render() {
    const { filter } = this.props;
    const { isEvergreen, query, queryBy } = this.state;

    return (
      <div className="d-flex flex-column mx-4">
        <Label className="font-weight-bold mt-4">Search by...</Label>
        <ButtonGroup className="w-100">
          <Button
            className="rounded-left w-50"
            color={this.isQueryByAsset ? 'primary' : 'secondary'}
            outline={!this.isQueryByAsset}
            onClick={this.changeQueryBy}
            data-test-id={`amp-modal-button-${AMP_QUERIES.ASSET_ID}`}
          >
            {AMP_QUERIES.ASSET_ID}
          </Button>
          <Button
            className="rounded-right w-50"
            color={!this.isQueryByAsset ? 'primary' : 'secondary'}
            outline={this.isQueryByAsset}
            onClick={this.changeQueryBy}
            data-test-id={`amp-modal-button-${AMP_QUERIES.WORKFRONT_ID}`}
          >
            {AMP_QUERIES.WORKFRONT_ID}
          </Button>
        </ButtonGroup>
        <Label className="font-weight-bold mt-4" for="queryInput">
          {queryBy}
        </Label>
        <Input
          className="rounded w-100"
          data-test-id="amp-modal-query-input"
          id="queryInput"
          name="query"
          onChange={this.handleInputChange}
          onKeyPress={this.handleKeyPress}
          type="text"
          value={query}
        />
        {this.isQueryByAsset && (
          <FormGroup check inline className="mt-2">
            <Label check>
              <Input
                checked={isEvergreen}
                data-test-id="amp-modal-evergreen-input"
                name="isEvergreen"
                onChange={this.handleInputChange}
                type="checkbox"
              />
              Search for evergreen asset
            </Label>
          </FormGroup>
        )}
        <ButtonGroup className="mt-4 ml-auto">
          <ClearButton
            className="mr-2"
            data-test-id="amp-modal-button-wr-clear-all"
            onClick={this.resetInputState}
          >
            Clear All
          </ClearButton>
          <PrimaryButton
            className="rounded"
            color="primary"
            data-test-id="amp-modal-button-wr-search"
            onClick={this.queryAMP}
          >
            Search
          </PrimaryButton>
        </ButtonGroup>
        {!this.isQueryByAsset && (
          <FormGroup>
            <Label className="font-weight-bold mt-4" for="filterInput">
              Filter
            </Label>
            <Input
              data-test-id="amp-modal-filter-dropdown"
              id="filterInput"
              onChange={this.handleFilterChange}
              type="select"
              value={filter}
            >
              {AMP_FILTER_OPTIONS.map(value => (
                <option key={value}>{value}</option>
              ))}
            </Input>
          </FormGroup>
        )}
      </div>
    );
  }
}
